import { Box, makeStyles, Typography } from "@material-ui/core";
import dayjs from "dayjs";
import React, { FC, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { useRecoilValue } from "recoil";

import { InvestmentProposal } from "@/api/generated";
import { MessageCard } from "@/components/message/MessageCard";
import { MessageSkeleton } from "@/components/message/MessageSkeleton";
import { ProposalLoadError } from "@/components/offer/ProposalLoadError";
import CustomPagination from "@/components/Pagination";
import { layoutTokens, semanticColors as colors, textStyles } from "@/designTokens";
import { useDevice } from "@/hooks/useDevice";
import { useGetInvestmentProposals } from "@/hooks/useGetInvestmentProposals";
import { useMasterAgentSearchTagMap } from "@/hooks/useGetMasterData";
import { useGetUserTotalReadAndMatchCount } from "@/hooks/useGetUserTotalReadAndMatchCount";
import { useUpdateInvestmentRead } from "@/hooks/useUpdateInvestmentRead";
import theme from "@/theme";
import { AppEventTokenEnum, sendEventToAppMeasurementTool } from "@/utilities/capacitorLogic";
import { calculatePaginationInvestmentProposal } from "@/utilities/pagination";
import { UserState } from "@/view_models/atoms";

import OfferMessageImage from "@assets/image/offer_message.png";

import { MessageEmpty } from "../MessageEmpty";

import { InvestmentDetailPane } from "./InvestmentDetailPane";

const useStyles = makeStyles({
  root: { padding: "24px 24px 0", [theme.breakpoints.up("md")]: { padding: "32px 0" } },
  header: { ...textStyles["PC/Heading/H2"], marginBottom: layoutTokens.space[24] },
  columns: {
    display: "flex",
    alignItems: "flex-start",
    gap: layoutTokens.space[40],
    [theme.breakpoints.between("md", 1200)]: { gap: layoutTokens.space[24] },
  },
  listPane: {
    width: 384,
    flexShrink: 0,
    position: "sticky",
    top: 88,
    maxHeight: "calc(100vh - 112px)",
    overflowY: "auto",
    borderRadius: layoutTokens.radius.lg,
    background: colors["surface/card"],
    [theme.breakpoints.between("md", 1200)]: { width: "36%" },
  },
  detailPane: { flex: 1, minWidth: 0 },
  intro: {
    display: "flex",
    alignItems: "center",
    gap: layoutTokens.space[12],
    marginBottom: layoutTokens.space[24],
    [theme.breakpoints.up("md")]: { gap: layoutTokens.space[16] },
  },
  introImage: { width: 64, height: "auto", flexShrink: 0, display: "block" },
  introContent: { flex: 1, minWidth: 0 },
  introTitle: {
    ...textStyles["SP/Heading/H3"],
    color: colors["text/heading"],
    marginBottom: layoutTokens.space[8],
    "& span": { display: "inline-block" },
    [theme.breakpoints.up("md")]: textStyles["PC/Heading/H3"],
  },
  introDescription: {
    ...textStyles["SP/Body/Small"],
    color: colors["text/secondary"],
    [theme.breakpoints.up("md")]: textStyles["PC/Body/Small"],
  },
  cards: {
    display: "flex",
    flexDirection: "column",
    gap: layoutTokens.space[12],
    [theme.breakpoints.up("md")]: { gap: 0 },
  },
});

// SP は同じ受信箱から詳細へ、PC は選択した1件を右ペインに表示する。
export const OfferPcView: FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { isMobile } = useDevice();
  const user = useRecoilValue(UserState);
  const { tagIdToNameMap } = useMasterAgentSearchTagMap();
  const { investmentProposals, loading, loadError, fetchInvestmentProposals } = useGetInvestmentProposals();
  useGetUserTotalReadAndMatchCount();
  const updateInvestmentRead = useUpdateInvestmentRead();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const queryPage = Number(new URLSearchParams(location.search).get("page")) || 1;
  const [currentPage, setCurrentPage] = useState(queryPage);
  const { currentInvestmentProposals, totalPages } = calculatePaginationInvestmentProposal(
    investmentProposals,
    currentPage
  );

  useEffect(() => {
    if (investmentProposals.length > 0 && !investmentProposals.some((proposal) => Number(proposal.id) === selectedId)) {
      setSelectedId(Number(investmentProposals[0].id));
    }
  }, [investmentProposals, selectedId]);

  const handleSelect = (proposal: InvestmentProposal) => {
    const id = Number(proposal.id);
    const params = user?.id ? { user_id: user.id } : undefined;
    sendEventToAppMeasurementTool(AppEventTokenEnum.ReadProposal, params);
    if (isMobile) {
      history.push(`/investment/${id}`);
    } else {
      setSelectedId(id);
      if (!proposal.is_user_read) updateInvestmentRead(id).then(() => fetchInvestmentProposals());
    }
  };

  const renderList = () => (
    <Box className={classes.cards}>
      {(isMobile ? currentInvestmentProposals : investmentProposals).map((proposal) => {
        const date = dayjs(proposal.proposal_date);
        const age = dayjs().startOf("day").diff(date.startOf("day"), "day");
        const arrivalLabel = proposal.proposal_date && date.isValid()
          ? age === 0 ? "本日届いたメッセージ" : age > 0 && age <= 6 ? "1週間以内のメッセージ" : undefined
          : undefined;
        const strengthLabels = (proposal.agent?.company?.company_agent_search_tag_ids ?? [])
          .map((id) => tagIdToNameMap[id])
          .filter((label): label is string => Boolean(label));
        return (
          <MessageCard
            key={proposal.id}
            name={`${proposal.agent?.family_name ?? ""}${proposal.agent?.first_name ?? ""}`}
            company={proposal.agent?.company?.name}
            image={proposal.agent?.agent_profile_image?.image_url}
            subject={proposal.short_appeal_message}
            date={proposal.proposal_date}
            isRead={proposal.is_user_read}
            isMatched={proposal.is_matched}
            arrivalLabel={arrivalLabel}
            strengthLabels={strengthLabels}
            selected={isMobile ? undefined : Number(proposal.id) === selectedId}
            onClick={() => handleSelect(proposal)}
          />
        );
      })}
    </Box>
  );

  const renderContent = () => {
    if (loading && investmentProposals.length === 0)
      return isMobile ? (
        <MessageSkeleton />
      ) : (
        <Box className={classes.columns}>
          <Box className={classes.listPane}>
            <MessageSkeleton />
          </Box>
          <Box className={classes.detailPane}>
            <MessageSkeleton detail />
          </Box>
        </Box>
      );
    if (loadError && investmentProposals.length === 0) return <ProposalLoadError onRetry={fetchInvestmentProposals} />;
    if (investmentProposals.length === 0) return <MessageEmpty />;
    return (
      <>
        {loadError && <ProposalLoadError compact onRetry={fetchInvestmentProposals} />}
        <Box component="section" aria-labelledby="offer-message-heading" className={classes.intro}>
          <img src={OfferMessageImage} alt="" className={classes.introImage} />
          <Box className={classes.introContent}>
            <Typography component="h2" id="offer-message-heading" className={classes.introTitle}>
              あなたにメッセージが<span>届いています！</span>
            </Typography>
            <Typography className={classes.introDescription}>
              あなたの情報を見た認定プランナーからメッセージが届いています。
            </Typography>
          </Box>
        </Box>
        {isMobile ? (
          <>
            {renderList()}
            {totalPages > 1 && (
              <Box pt={2}>
                <CustomPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={(page) => {
                    setCurrentPage(page);
                    const params = new URLSearchParams(location.search);
                    params.set("page", String(page));
                    history.replace({ search: params.toString() });
                  }}
                />
              </Box>
            )}
          </>
        ) : (
          <Box className={classes.columns}>
            <Box className={classes.listPane} data-testid="message-inbox">
              {renderList()}
            </Box>
            <Box className={classes.detailPane}>
              {selectedId !== null && (
                <InvestmentDetailPane
                  key={selectedId}
                  proposalId={selectedId}
                  onAfterApprove={fetchInvestmentProposals}
                  onAfterRefuse={fetchInvestmentProposals}
                />
              )}
            </Box>
          </Box>
        )}
      </>
    );
  };
  return (
    <Box className={classes.root}>
      {!isMobile && (
        <Typography component="h1" className={classes.header}>
          メッセージ
        </Typography>
      )}
      {renderContent()}
    </Box>
  );
};
